import { TemplateVariable } from 'libs/home-building/src/lib/models/template.models';

export interface S3Document {
  id: string;
  s3Key: string;
  directUrl: string;
}

export enum EnvelopeStatus {
  Pending = 'Pending',
  Sent = 'Sent',
  Delivered = 'Delivered',
  Complete = 'Complete',
  Voided = 'Voided',
  Declined = 'Declined',
  Expired = 'Expired'
}

/** Mirrors the backend MaxAutoResends guardrail for expired envelopes. */
export const MAX_AUTO_RESENDS = 2;

export enum RecipientStatus {
  Pending = 'Pending',
  Sent = 'Sent',
  Delivered = 'Delivered',
  Complete = 'Complete',
  Declined = 'Declined',
  Signed = 'Signed',
  AutoResponded = 'AutoResponded',
  AuthenticationFailed = 'AuthenticationFailed'
}

/** Matches backend SigningConstants.SignatureCompletionSource. */
export enum SignatureCompletionSource {
  Electronic = 'Electronic',
  ExecutedOnPaper = 'ExecutedOnPaper',
  Waived = 'Waived',
  RemovedFromEnvelope = 'RemovedFromEnvelope'
}

export enum DealDocumentSignatureFlowType {
  'None' = 0,
  'DocuSign' = 1,
  'DocuSignInPerson' = 3
}

/** Matches backend DealDocumentSignatureDto (JSON camelCase). */
export interface DealDocumentSignature {
  id: string;
  dealDocumentId: string;
  entityType: string;
  entityID: string;
  status: RecipientStatus;
  sequence?: number;
  failureReason?: string | null;
  completionSource: SignatureCompletionSource | string | null;
  completionNote: string | null;
  signee: {
    name?: string | null;
    email?: string | null;
  };
  createdAt?: string;
  lastModified: string | null;
  signedDate?: string | null;
}

export interface DocuSignInPersonSigningSession {
  envelopeId: string;
  signingUrl: string;
}

export interface DocumentTemplate {
  id: string;
  projectId: string;
  name: string;
  s3Document: {
    id: string;
    s3Key: string;
    directUrl: string;
  };
  templateVariable: {
    id: string;
    name: string;
  };
  sequence: number;
  isPositionLocked?: boolean;
}

export interface DocumentTemplatesResponse {
  message: string;
  data: DocumentTemplate[];
  status: number;
  success: boolean;
}

export interface CustomDocument {
  id?: string;
  customDocumentId: string;
  afterTemplateId: string;
  name: string;
  s3Document?: {
    id: string;
    s3Key: string;
    directUrl: string;
  };
  isCustom: boolean;
}

export interface GenerateContractPayload {
  templateId: string | null;
  s3DocumentId: string | null;
  type: 'template' | 'custom';
}

export interface GenerateContractRequest {
  documentPlacements: GenerateContractPayload[];
}

export type DealDocumentCustomTemplateState = {
  customDocumentId: string;
  anchorSystemTemplateId: string | null;
  customDocumentName: string;
};

export type DealDocumentTemplateState = {
  orderedTemplateIds: string[];
  customTemplates: DealDocumentCustomTemplateState[];
};

export const AMENDMENT_DOCUMENT_TYPE = 'AMENDMENT';
export const COOP_DOCUMENT_TYPE = 'COOP';

export type DealAmendmentUploadDraft = {
  documentId: string;
  documentName: string;
  s3Key: string;
  directUrl?: string;
};

export type DocumentTemplateWithSelected = DocumentTemplate & {
  isSelected: boolean;
  isCustom: boolean;
  isPositionLocked?: boolean;
};

export type GeneratedSchedulePeDocument = {
  documentId?: string;
  documentName?: string;
  DocumentId?: string;
  DocumentName?: string;
  documentUrl?: string;
};

export type DocumentPlacement = {
  templateId: string;
  s3DocumentId: string;
  type: 'template' | 'custom';
};

export interface miscellaneousDocument {
  id: string;
  projectId: string;
  name: string;
  s3Document: S3Document;
  templateVariable: TemplateVariable;
  createdAt: string;
  createdDate: string | null;
  updatedBy: string | null;
  updatedDate: string | null;
  sequence: number;
  fileType: string;
  documentType: string;
}

/** A "receives a copy" recipient of a co-op envelope, saved when it was sent. */
export interface DealDocumentCopyRecipient {
  name: string;
  email: string;
}

/** Matches backend DealDocumentsDto (JSON camelCase). */
export interface ApsDealDocument {
  id: string;
  dealId: string;
  templateId: string;
  s3DocumentId: string;
  pdfS3DocumentId?: string;
  signedS3DocumentId: string | null;
  status: EnvelopeStatus;
  envelopeId?: string | null;
  deliveryFailureReason?: string | null;
  deliveryFailedAt?: string | null;
  expiredAt: string | null;
  autoResendCount: number;
  lastAutoResendAt: string | null;
  documentType: string;
  signingType: DealDocumentSignatureFlowType;
  createdByUserId: string;
  updatedByUserId: string;
  createdAt: string;
  lastModified: string | null;
  templateName: string;
  s3Document: S3Document;
  signedS3Document: S3Document | null;
  createdByName: string;
  updatedByName: string;
  dealDocumentSignatures: DealDocumentSignature[];
  copyRecipients?: DealDocumentCopyRecipient[];
  pdfS3Document?: {
    id: string;
    directUrl: string;
    s3Key?: string;
  };
  externalSigning: ExternalDocument | null;
  generatedForLotNo?: string | null;
}

/** @deprecated Prefer ApsDealDocument. */
export type dealDocuments = ApsDealDocument;

/** True when this deal already has a non-APS document produced from the given project template. */
export function nonApsDealDocumentMatchesTemplate(doc: ApsDealDocument, template: miscellaneousDocument): boolean {
  const tid = doc.templateId?.trim();
  const tplId = template.id?.trim();
  if (tid && tplId && tid === tplId) {
    return true;
  }
  const dt = doc.documentType?.trim().toLowerCase();
  const tdt = template.documentType?.trim().toLowerCase();
  return Boolean(dt && tdt && dt === tdt);
}

/** Project COOP templates that do not yet have a matching generated deal document. */
export function filterMiscellaneousTemplatesPendingGeneration(
  templates: miscellaneousDocument[],
  dealDocumentsList: ApsDealDocument[]
): miscellaneousDocument[] {
  return templates.filter(
    template =>
      template.documentType?.trim().toUpperCase() === COOP_DOCUMENT_TYPE &&
      !dealDocumentsList.some(doc => nonApsDealDocumentMatchesTemplate(doc, template))
  );
}

export interface ExternalDocReq {
  note: string | null;
  externalDocumentId: string;
  approvalDate: string | null;
  apsSignDate: string | null;
}

/** Matches backend DealExternalSigningDto (JSON camelCase). */
export interface ExternalDocument {
  id: string;
  dealId: string;
  note: string | null;
  externalDocumentId: string;
  documentInfo: S3Document;
  createdAt: string;
  lastModified: string | null;
  createdBy: string;
  updatedBy: string | null;
}
